import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import * as Highcharts from 'highcharts';
import { catchError, debounceTime, distinctUntilChanged, Subscription, throwError } from 'rxjs';
import { AllProfitDetails } from '../../shared/models/statistics.model';
import { BasicUser } from '../../shared/models/user.model';
import { LocalStorageService } from '../../shared/services/localStorage.service';
import { ModalService } from '../../shared/services/modal.service';
import { NotificationService } from '../../shared/services/notification.service';
import { StatisticsService } from '../../shared/services/statistics.service';
declare const moment: any;

@Component({
  standalone: false,
  selector: 'app-dashboard-profit-widget',
  templateUrl: './dashboard-profit-widget.component.html',
  styleUrls: ['dashboard-profit-widget.component.scss', '../shared.scss'],
})
export class DashboardProfitWidgetComponent implements AfterViewInit, OnChanges, OnDestroy {
  Highcharts: typeof Highcharts = Highcharts;

  @ViewChild('dailyProfitChartContainer', { static: false }) dailyProfitChartContainer;
  @ViewChild('profitDetailStatisticPeriodModalTpl', { read: TemplateRef, static: false }) profitDetailStatisticPeriodModalTpl: any;

  @ViewChild('chartContainer', { static: false }) chartContainer;

  chart: Highcharts.Chart;
  chartOptions: Highcharts.Options = {
    title: { text: 'Profit', x: -20, style: { display: 'none' } },
    chart: { height: 190, style: { fontFamily: StatisticsService.chartFontFamily } },
    colors: StatisticsService.colors,
    xAxis: {
      type: 'datetime',
      gridLineWidth: 1,
      tickWidth: 0,
      allowDecimals: false,
      crosshair: StatisticsService.chartCrosshair,
    },
    yAxis: [{ title: { text: null } }],
    tooltip: StatisticsService.chartTooltip,
    legend: { align: 'center', borderWidth: 0 },
    series: [
      { type: 'areaspline', name: 'Total', lineWidth: 1, marker: { radius: 2 }, data: [] },
      { type: 'areaspline', name: 'MT', lineWidth: 1, marker: { radius: 2 }, data: [] },
      { type: 'areaspline', name: 'CLI', lineWidth: 1, marker: { radius: 2 }, data: [] },
      { type: 'areaspline', name: 'MO', lineWidth: 1, marker: { radius: 2 }, data: [] },
    ],
    credits: { enabled: false },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.3,
      },
    },
  };

  profitDetailStatisticPeriodDates = null;
  profitDetailStatisticPeriodRows: AllProfitDetails[] = [];
  profitDetailStatisticPeriodLoading = false;
  profitDetailStatisticPeriodSum = 0;
  profitDetailStatisticPeriodSort = '';
  profitDetailStatisticPeriodSearch = '';

  @Input() from: string;
  @Input() to: string;
  @Input() users: BasicUser[];

  start;
  end;

  loading = false;

  private request: Subscription;

  searchControl = new FormControl();
  private searchSubscription: Subscription;

  @HostListener('window:resize') onResize() {
    this.resize();
  }
  resize() {
    if (!this.chartContainer || !this.chart) {
      return;
    }
    this.chart.setSize(this.chartContainer.nativeElement.offsetWidth, this.chartOptions.chart.height as number);
  }

  constructor(
    public statistics: StatisticsService,
    public notificationService: NotificationService,
    public modal: ModalService,
    private localStorage: LocalStorageService,
    private cdr: ChangeDetectorRef
  ) {
    this.profitDetailStatisticPeriodSort = localStorage.get('db_profit_sort', '-profit');
    this.profitDetailStatisticPeriodDates = [
      moment().startOf('month').format('YYYY-MM-DDT00:00:00'),
      moment().endOf('month').format('YYYY-MM-DDT23:59:59'),
    ];
    this.start = moment().startOf('month').unix();
    this.end = moment().endOf('month').unix();
  }

  ngAfterViewInit() {
    this.searchSubscription = this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(searchValue => {
      this.onChangeInputSearch(searchValue);
    });
    this.resize();
    this.update();

    this.cdr.detectChanges();
  }

  update() {
    this.loading = true;
    if (this.request && !this.request.closed) {
      this.request.unsubscribe();
    }
    const userIds = this.users.map(_ => _.id);
    this.request = this.statistics
      .dailyProfit(this.from, this.to, userIds)
      .pipe(
        catchError(e => {
          this.loading = false;
          this.notificationService.error({
            title: 'Dashboard',
            message: 'An error occurred while loading statistics',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          this.chart.showLoading('Error on loading');
          return throwError(() => e);
        })
      )
      .subscribe(data => {
        this.loading = false;
        if (!this.chart) {
          return;
        }
        this.chart.hideLoading();
        if (data.total.length) {
          this.chart.update({
            xAxis: { tickInterval: data.tickInterval, labels: { step: data.labelStep } },
            series: [
              { type: 'areaspline', data: data.total },
              { type: 'areaspline', data: data.mt, visible: false },
              { type: 'areaspline', data: data.cli, visible: false },
              { type: 'areaspline', data: data.mo, visible: false },
            ],
          });
        } else {
          if (!this.chart) {
            return;
          }
          this.chart.showLoading('No data');
        }
      });
  }

  updateProfitDetailStatistics() {
    this.profitDetailStatisticPeriodLoading = true;
    const start = this.profitDetailStatisticPeriodDates !== null ? this.profitDetailStatisticPeriodDates[0] : '';
    const end = this.profitDetailStatisticPeriodDates !== null ? this.profitDetailStatisticPeriodDates[1] : '';
    this.statistics.profitDetails(start, end).subscribe(
      rows => {
        this.profitDetailStatisticPeriodLoading = false;
        this.profitDetailStatisticPeriodSum = rows.length
          ? parseFloat(
              rows
                .map(_ => _.profit)
                .reduce((sum, _) => sum + _)
                .toFixed(3)
            )
          : 0;
        this.profitDetailStatisticPeriodRows = rows.map(_ => {
          _.profit = _.profit ? parseFloat(_.profit.toFixed(3)) : 0;
          return _;
        });
      },
      error => {
        this.profitDetailStatisticPeriodLoading = false;
        this.notificationService.error({
          title: 'Dashboard',
          message: 'An error occurred while loading profit details',
          requestMessage: error.statusText,
          requestCode: error.status,
        });
      }
    );
  }

  onClickButtonOpenProfitDetail() {
    this.modal
      .alert()
      .isBlocking(false)
      .dialogClass('modal-dialog full-modal')
      .component(this.profitDetailStatisticPeriodModalTpl)
      .open()
      .onDestroy.subscribe(() => {
        this.profitDetailStatisticPeriodDates = [
          moment().startOf('month').format('YYYY-MM-DDT00:00:00'),
          moment().endOf('month').format('YYYY-MM-DDT23:59:59'),
        ];
      });
    this.updateProfitDetailStatistics();
  }

  onClickButtonExportProfitDetail() {
    const start = this.profitDetailStatisticPeriodDates !== null ? this.profitDetailStatisticPeriodDates[0] : '';
    const end = this.profitDetailStatisticPeriodDates !== null ? this.profitDetailStatisticPeriodDates[1] : '';
    this.profitDetailStatisticPeriodLoading = true;
    this.statistics.profitDetailsExport(start, end).subscribe(
      blob => {
        const url = window.URL.createObjectURL(blob);
        this.profitDetailStatisticPeriodLoading = false;
        window.open(url);
      },
      error => {
        this.profitDetailStatisticPeriodLoading = false;
        this.notificationService.error({
          title: 'Dashboard',
          message: 'An error occurred while export profit details',
          requestMessage: error.statusText,
          requestCode: error.status,
        });
      }
    );
  }

  onChangeDateProfitDetailStatistic(dates) {
    if (typeof dates === 'string') {
      return;
    }
    if (dates.startStr === null || dates.endStr === null) {
      dates.startStr = moment().startOf('day').format('YYYY-MM-DDT00:00:00');
      dates.endStr = moment().endOf('day').format('YYYY-MM-DDT23:59:59');
    }
    this.profitDetailStatisticPeriodDates = [dates.startStr, dates.endStr];
    this.updateProfitDetailStatistics();
  }

  setProfitDetailStatisticPeriodSort(column) {
    let sort = this.profitDetailStatisticPeriodSort;
    if (sort === '-' + column) {
      sort = column;
    } else if (sort === column) {
      sort = '-' + column;
    } else {
      sort = column;
    }
    this.profitDetailStatisticPeriodSort = sort;
    this.localStorage.set('db_profit_sort', sort);
  }

  onChangeInputSearch(phrase) {
    if (phrase instanceof Event) {
      return;
    }
    this.profitDetailStatisticPeriodSearch = phrase;
  }

  setChart(chart: Highcharts.Chart) {
    this.chart = chart;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.users && changes.users.previousValue !== undefined) ||
      (changes.from && changes.from.previousValue !== undefined) ||
      (changes.to && changes.to.previousValue !== undefined)
    ) {
      this.update();
    }
  }

  ngOnDestroy() {
    this.chart = null;

    if (this.searchSubscription && !this.searchSubscription.closed) {
      this.searchSubscription.unsubscribe();
    }

    if (this.request && !this.request.closed) {
      this.request.unsubscribe();
    }
  }
}
